import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaCrown, FaTimes, FaCheck, FaLock } from 'react-icons/fa';
import { toast } from 'sonner';
import { trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import {
  SUBSCRIPTIONS,
  SUBSCRIPTION_ORDER,
  SUBSCRIPTION_TYPES,
  getSubscriptionType,
} from '../config/subscriptions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// localStorage-nøgle brugt til at bære et gæste-valgt abonnement gennem
// signup/login-flowet. Læses af BillingResumeCheckoutPage.
export const PENDING_CHECKOUT_KEY = 'pending_checkout';

/**
 * UpgradeModal (refactored 01-Jul-2026)
 *
 * Dynamisk drevet af `config/subscriptions.js`. Viser alle 3 tiers
 * (Gratis / Light / Pro) — Light gråtonet med "Kommer snart" indtil
 * `available=true` sættes i konfigurationen. Alle fordele, priser og
 * CTA-labels kommer fra config så ingen tekster er hardcodede her.
 *
 * Kontekst-afhængighed via `context`-prop:
 *   'general'         → "Vælg det abonnement der passer dig"
 *   'locked_feature'  → "Denne funktion kræver et abonnement"
 *   Andre nøgler kortlægges til locked_feature med en specifik beskrivelse.
 *
 * `currentUser`-prop bruges til at markere brugerens nuværende tier så
 * "Fortsæt gratis"-knappen f.eks. ikke vises for Pro-brugere.
 */

/**
 * Kontekst-afhængige beskeder — bygges dynamisk ud fra brugerens aktuelle
 * subscription_type + hvilken tier `feature` kræver.
 *
 * `requiredTier` fortæller hvilket abonnement der låser den ønskede funktion op:
 *   'auth'  → funktionen kræver blot at man er logget ind (fx Indkøbsliste,
 *             Favoritter). Vises kun for anonyme.
 *   'light' → Light ELLER Pro giver adgang (fx ubegrænset private opskrifter,
 *             ingen bundreklamer).
 *   'pro'   → Kun Pro giver adgang (fx premium-features der ikke kommer til Light).
 *
 * `reason` beskriver konteksten (fx 'recipe_locked') og styrer HEADING'en.
 * BODY-teksten bygges dynamisk baseret på requiredTier + currentTier.
 */
const REASON_HEADINGS = {
  general: 'Vælg det abonnement der passer dig',
  locked_feature: 'Denne funktion kræver et abonnement',
  recipe_locked: 'Denne opskrift kræver et abonnement',
  recipe_limit: 'Du har nået grænsen for gratis opskrifter',
  publish: 'Deling kræver et abonnement',
};

function buildContextBody({ requiredTier, currentTier, reason }) {
  // Anonym først — uanset hvilken reason caller sender, er første skridt at
  // logge ind. Denne rækkefølge dækker også nav-lock-cases der bruger
  // reason='general' som fallback.
  if (currentTier === 'anonymous') {
    return 'Denne funktion kræver, at du logger ind.';
  }
  // Generel visning (fx "Se abonnementer" i menu) — ingen konkret gate.
  if (reason === 'general') {
    return 'SlushBook findes i flere udgaver. Vælg det der passer bedst — du kan opgradere når som helst.';
  }
  // Registreret Free: næste skridt afhænger af hvor højt funktionen låser.
  if (currentTier === 'free') {
    if (requiredTier === 'pro') {
      return 'Denne funktion kræver et Pro-abonnement.';
    }
    return 'Denne funktion kræver et Light- eller Pro-abonnement.';
  }
  // Light: kun Pro-features tilbage at opgradere til.
  if (currentTier === 'light') {
    return 'Denne funktion kræver et Pro-abonnement.';
  }
  // Fallback (bør ikke nås — Pro/employees ser ikke modalen).
  return 'Opgradér for at få adgang. Se hvilke abonnementer der åbner denne funktion nedenfor.';
}

const UpgradeModal = ({
  isOpen,
  onClose,
  reason = 'general',
  currentUser = null,
  /**
   * Hvilket abonnement funktionen kræver — driver den kontekstafhængige besked.
   * 'auth'  = kræver blot login
   * 'light' = kræver Light eller Pro (default — dækker ALLE nuværende
   *           betalte funktioner, da Light og Pro har samme adgang)
   * 'pro'   = kræver KUN Pro (fx fremtidige eksklusive Pro-features)
   */
  requiredTier = 'light',
}) => {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const currentSubscription = getSubscriptionType(currentUser);
  // Normaliseret tier-navn til beskedbyggeren:
  // "anonymous" for !user, ellers subscription_type.
  const currentTier = !currentUser ? 'anonymous' : currentSubscription;
  const isAnonymous = currentTier === 'anonymous';

  // Heading er reason-baseret, men anon-brugere får en login-fokuseret
  // headline i stedet så matcher body-teksten.
  const heading = currentTier === 'anonymous'
    ? 'Log ind for at fortsætte'
    : (REASON_HEADINGS[reason] || REASON_HEADINGS.general);
  const body = buildContextBody({ requiredTier, currentTier, reason });
  const context = { heading, body };

  // view_pricing event
  useEffect(() => {
    if (!isOpen) return;
    trackEvent(ANALYTICS_EVENTS.VIEW_PRICING, {
      source: reason,
      default_plan: 'monthly',
      current_tier: currentTier,
    });
  }, [isOpen, reason, currentTier]);

  // Byg tier-cards fra config. Skal reagere hvis Light aktiveres senere.
  const tierCards = useMemo(
    () => SUBSCRIPTION_ORDER.map((key) => SUBSCRIPTIONS[key]),
    []
  );

  if (!isOpen) return null;

  const handleCheckout = async (planKey) => {
    // Gæste-gate: en anonym bruger må aldrig ramme /billing/checkout direkte
    // (backend returnerer 401). Gem plan-valget og send dem via signup →
    // /billing/resume-checkout som fortsætter til Stripe efter auth.
    if (isAnonymous) {
      try {
        localStorage.setItem(
          PENDING_CHECKOUT_KEY,
          JSON.stringify({ plan: planKey, ts: Date.now() })
        );
      } catch (_e) { /* localStorage disabled — ok, resume-siden håndterer det */ }
      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        plan: planKey,
        source: reason,
        auth_state: 'anonymous_deferred',
      });
      onClose?.();
      navigate('/signup?next=/billing/resume-checkout');
      return;
    }

    setLoading(true);
    // Prisværdi til analytics — matches PLANS[] i stripe_routes.py
    const analyticsValue = planKey === 'yearly' ? 399 : planKey === 'light_monthly' ? 25 : 49;
    trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
      plan: planKey,
      value: analyticsValue,
      currency: 'DKK',
      source: reason,
    });
    try {
      const token = localStorage.getItem('session_token');
      const { data } = await axios.post(
        `${API}/billing/checkout`,
        { plan: planKey, origin_url: window.location.origin },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout failed:', err);
      const detail = err.response?.data?.detail || 'Kunne ikke starte checkout';
      toast.error(detail);
      setLoading(false);
    }
  };

  const handleTierCta = (tier) => {
    if (tier.ctaAction === 'checkout') {
      // Bestem plan-key: Light har kun én; Pro følger monthly/yearly-toggle.
      let planKey;
      if (tier.key === SUBSCRIPTION_TYPES.LIGHT) {
        planKey = tier.planKey || 'light_monthly';
      } else if (tier.key === SUBSCRIPTION_TYPES.PRO) {
        planKey = selectedPlan === 'yearly' ? 'yearly' : 'monthly';
      } else {
        planKey = tier.planKey || 'monthly';
      }
      handleCheckout(planKey);
      return;
    }
    if (tier.ctaAction === 'notify') {
      // Placeholder — bruges ikke længere for Light (Light er aktiv),
      // men bevares for evt. fremtidige "coming soon"-planer.
      toast.success('Vi giver besked når det er klar 👍');
      return;
    }
    // 'dismiss' eller ukendt → luk popup
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      data-testid="upgrade-modal"
    >
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-4xl w-full my-8 relative shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
          data-testid="upgrade-modal-close-btn"
          aria-label="Luk"
        >
          <FaTimes size={20} />
        </button>

        {/* Header — kontekst-afhængig */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mb-3">
            {reason === 'general' ? (
              <FaCrown className="text-white text-2xl" />
            ) : (
              <FaLock className="text-white text-xl" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="upgrade-modal-title">
            {context.heading}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{context.body}</p>
        </div>

        {/* Årlig/månedlig toggle — vises kun når Pro er en mulighed */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-full p-1" data-testid="plan-toggle">
            <button
              onClick={() => setSelectedPlan('monthly')}
              data-testid="plan-monthly-btn"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
                selectedPlan === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
            >
              Månedlig
            </button>
            <button
              onClick={() => setSelectedPlan('yearly')}
              data-testid="plan-yearly-btn"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
                selectedPlan === 'yearly' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
            >
              Årlig <span className="text-emerald-600 text-xs ml-1">-31%</span>
            </button>
          </div>
        </div>

        {/* 3 tier-cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {tierCards.map((tier) => {
            // "Dit nuværende abonnement"-markering skal matche subscription
            // (ikke 'anonymous' — anonyme har ingen aktuel tier at highlighte).
            const isCurrent = tier.key === currentSubscription && currentTier !== 'anonymous';
            const isComingSoon = tier.comingSoon === true;
            const disabled = !tier.available || isCurrent;

            // Vis månedspris eller årspris afh. af toggle for Pro
            const displayPrice =
              tier.key === SUBSCRIPTION_TYPES.PRO && selectedPlan === 'yearly'
                ? tier.yearlyPrice || tier.price
                : tier.price;
            const displayHint =
              tier.key === SUBSCRIPTION_TYPES.PRO && selectedPlan === 'yearly'
                ? tier.yearlyHint || tier.priceHint
                : tier.priceHint;

            return (
              <div
                key={tier.key}
                data-testid={`tier-card-${tier.key}`}
                className={`relative flex flex-col rounded-2xl border-2 p-5 ${
                  tier.highlight
                    ? 'border-orange-400 bg-gradient-to-b from-orange-50 to-white'
                    : 'border-gray-200 bg-white'
                } ${isComingSoon ? 'opacity-75' : ''}`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    {tier.highlightLabel || 'Mest populær'}
                  </div>
                )}
                {isComingSoon && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                    data-testid={`tier-coming-soon-${tier.key}`}
                  >
                    {tier.comingSoonLabel || 'Kommer snart'}
                  </div>
                )}

                <div className="mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{tier.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{tier.tagline}</p>
                </div>

                <div className="mb-4">
                  <div className="text-3xl font-extrabold text-gray-900">
                    {displayPrice}
                  </div>
                  <div className="text-xs text-gray-500">{displayHint}</div>
                </div>

                <ul className="space-y-2 mb-5 flex-1">
                  {tier.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <FaCheck className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleTierCta(tier)}
                  disabled={disabled || loading}
                  data-testid={`tier-cta-${tier.key}`}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition ${
                    isCurrent
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : tier.highlight
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white shadow'
                        : disabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {isCurrent
                    ? 'Dit nuværende abonnement'
                    : loading && tier.ctaAction === 'checkout'
                      ? 'Sender dig til Stripe…'
                      : tier.ctaLabel}
                </button>
              </div>
            );
          })}
        </div>

        {/* Gæste-hint: fortæl anonyme brugere at klik på Light/Pro starter
            med kontooprettelse — så de ikke bliver overraskede. Vises IKKE
            for logged-in brugere, da flowet der er direkte Stripe checkout. */}
        {isAnonymous && (
          <p
            className="text-center text-xs text-gray-500 mt-4"
            data-testid="upgrade-modal-anon-hint"
          >
            Opret en konto eller{' '}
            <button
              type="button"
              onClick={() => { onClose?.(); navigate('/login?mode=login&next=/billing/resume-checkout'); }}
              className="underline text-gray-700 hover:text-gray-900"
              data-testid="upgrade-modal-anon-login-link"
            >
              log ind
            </button>
            {' '}for at fortsætte til betaling.
          </p>
        )}

        {/* Diskret "måske senere" — beholdes for at bevare tidligere UX */}
        <div className="text-center mt-4">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            data-testid="upgrade-modal-later-btn"
          >
            Måske senere
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
